/**
 * Puntaje 0..100 de una medicion de red.
 *
 * Es la copia en el navegador de `CloudpbxOperator.Network.Score`. Los dos
 * lados calculan lo mismo: el servidor para el histórico, este para el
 * indicador en vivo, que tiene que reaccionar sin esperar a que la ventana
 * de 5 minutos cierre. Si se tocan los umbrales hay que tocarlos en ambos.
 */

export type NetworkLevel = 'excellent' | 'good' | 'fair' | 'bad' | 'unknown';

export const EXCELLENT = 85;
export const GOOD = 70;
export const FAIR = 50;

/** Sin dato de perdida no se puede certificar una conexion como excelente. */
export const NO_UDP_CEILING = 85;

const LOSS_PER_PCT = 20;
const LOSS_CAP = 60;

const JITTER_FREE_MS = 20;
const JITTER_PER_MS = 0.8;
const JITTER_CAP = 35;

const RTT_FREE_MS = 150;
const RTT_PER_MS = 0.05;
const RTT_CAP = 15;

const TIMEOUT_PENALTY = 5;
const TIMEOUT_CAP = 20;

export interface ScoreInput {
    samples: number;
    timeouts?: number;
    rttAvg?: number | null;
    jitterAvg?: number | null;
    udpProbeOk?: boolean;
    udpRttAvg?: number | null;
    udpJitterAvg?: number | null;
    udpLossPct?: number | null;
}

function penalty(value: number | null | undefined, free: number, per: number, cap: number): number {
    if (value === null || value === undefined) {
        return 0;
    }

    return Math.min(cap, Math.max(0, value - free) * per);
}

function lossPenalty(loss: number | null | undefined): number {
    if (loss === null || loss === undefined || loss <= 0) {
        return 0;
    }

    return Math.min(LOSS_CAP, loss * LOSS_PER_PCT);
}

function timeoutPenalty(timeouts: number | undefined): number {
    if (!timeouts) {
        return 0;
    }

    return Math.min(TIMEOUT_CAP, timeouts * TIMEOUT_PENALTY);
}

/** `null` cuando no hubo ninguna muestra. */
export function score(input: ScoreInput): number | null {
    if (!input.samples) {
        return null;
    }

    const udp = input.udpProbeOk === true;
    const jitter = udp ? (input.udpJitterAvg ?? input.jitterAvg) : input.jitterAvg;
    const rtt = udp ? (input.udpRttAvg ?? input.rttAvg) : input.rttAvg;
    const loss = udp ? input.udpLossPct : null;

    const total = lossPenalty(loss)
        + penalty(jitter, JITTER_FREE_MS, JITTER_PER_MS, JITTER_CAP)
        + penalty(rtt, RTT_FREE_MS, RTT_PER_MS, RTT_CAP)
        + timeoutPenalty(input.timeouts);

    const raw = Math.round(100 - total);
    const capped = udp ? raw : Math.min(raw, NO_UDP_CEILING);

    return Math.max(0, Math.min(100, capped));
}

export function level(value: number | null | undefined): NetworkLevel {
    if (value === null || value === undefined) {
        return 'unknown';
    }
    if (value >= EXCELLENT) {
        return 'excellent';
    }
    if (value >= GOOD) {
        return 'good';
    }
    if (value >= FAIR) {
        return 'fair';
    }

    return 'bad';
}

export const LEVEL_LABEL: Record<NetworkLevel, string> = {
    excellent: 'Excelente',
    good: 'Buena',
    fair: 'Regular',
    bad: 'Mala',
    unknown: 'Sin datos',
};

/**
 * Que significa cada nivel, en castellano de persona.
 *
 * Describe lo que se midio, en pasado, y nada mas. No se afirma nada sobre
 * llamadas: esta herramienta no mide audio. Prometer "no vas a tener
 * problemas en las llamadas" es una afirmacion que no podemos sostener con
 * el dato que tenemos, y que despues se usa en contra.
 */
export const LEVEL_MEANING: Record<NetworkLevel, string> = {
    excellent: 'La conexión respondió rápido y de forma estable durante la medición.',
    good: 'La conexión respondió bien, con variaciones leves.',
    fair: 'La conexión respondió con demoras o variaciones marcadas.',
    bad: 'La conexión respondió con demoras altas o con cortes.',
    unknown: 'Todavía no hay suficientes mediciones.',
};

/** Traduce la demora a palabras, que es como la lee una persona. */
export function latencyWord(ms: number | null | undefined): string | undefined {
    if (ms === null || ms === undefined) {
        return undefined;
    }
    if (ms < 100) {
        return 'muy buena';
    }
    if (ms < 200) {
        return 'buena';
    }
    if (ms < 400) {
        return 'alta';
    }

    return 'muy alta';
}

/** Lo mismo para el jitter, que en la pantalla se llama estabilidad. */
export function stabilityWord(ms: number | null | undefined): string | undefined {
    if (ms === null || ms === undefined) {
        return undefined;
    }
    if (ms < 20) {
        return 'muy estable';
    }
    if (ms < 40) {
        return 'estable';
    }
    if (ms < 80) {
        return 'irregular';
    }

    return 'muy irregular';
}

export const LEVEL_CLASS: Record<NetworkLevel, string> = {
    excellent: 'bg-green-100 text-green-800',
    good: 'bg-lime-100 text-lime-800',
    fair: 'bg-yellow-100 text-yellow-800',
    bad: 'bg-red-100 text-red-800',
    unknown: 'bg-gray-100 text-gray-600',
};

/**
 * Jitter al estilo RFC 3550 simplificado: desviacion media absoluta entre
 * muestras consecutivas. No es la varianza suavizada del RTP, pero responde
 * igual de rapido y se explica en una linea.
 */
export function jitterOf(samples: number[]): number | null {
    if (samples.length < 2) {
        return null;
    }

    let total = 0;
    for (let i = 1; i < samples.length; i += 1) {
        total += Math.abs(samples[i] - samples[i - 1]);
    }

    return Math.round(total / (samples.length - 1));
}

/** Pico de desviacion entre dos muestras seguidas, no el promedio. */
export function jitterMaxOf(samples: number[]): number | null {
    if (samples.length < 2) {
        return null;
    }

    let peak = 0;
    for (let i = 1; i < samples.length; i += 1) {
        peak = Math.max(peak, Math.abs(samples[i] - samples[i - 1]));
    }

    return Math.round(peak);
}

export function averageOf(samples: number[]): number | null {
    if (!samples.length) {
        return null;
    }

    return Math.round(samples.reduce((acc, value) => acc + value, 0) / samples.length);
}
